// THROWAWAY test page for the engine spike (SPIKE.md). Delete this folder
// once the real converter UI exists. Uses the engine only through EngineClient.

import { useEffect, useMemo, useRef, useState } from 'react';
import { EngineClient } from '../../engine/worker/client';
import type { Capabilities, JobResult, MediaInfo, Progress } from '../../engine/types';
import { EngineError } from '../../engine/errors';
import { PRESETS } from '../../presets/presets';
import { browserLabel, formatBytes, formatSeconds } from '../../lib/format';

type JobName = 'MP4 <20MB' | 'GIF';
type Status = 'idle' | 'running' | 'done' | 'error';

const CLIPS = ['iPhone HEVC', 'Screen rec', 'Long 1080p', 'Other'] as const;

function Yes({ ok }: { ok: boolean }) {
  return <span className={ok ? 'yes' : 'no'}>{ok ? 'Yes' : 'No'}</span>;
}

export function SpikePage() {
  const engine = useMemo(() => new EngineClient(), []);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [info, setInfo] = useState<MediaInfo | null>(null);
  const [clip, setClip] = useState<(typeof CLIPS)[number]>('iPhone HEVC');
  const [status, setStatus] = useState<Status>('idle');
  const [jobName, setJobName] = useState<JobName | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [result, setResult] = useState<JobResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const jobId = useRef<number | null>(null);
  const startedAt = useRef(0);

  useEffect(() => {
    engine.capabilities().then(setCaps, (e: EngineError) => setError(e.message));
  }, [engine]);

  useEffect(() => {
    if (!result) return;
    const url = URL.createObjectURL(result.blob);
    setResultUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [result]);

  useEffect(() => {
    if (status !== 'running') return;
    const t = setInterval(() => setElapsed((performance.now() - startedAt.current) / 1000), 200);
    return () => clearInterval(t);
  }, [status]);

  async function pick(f: File | undefined) {
    setFile(f ?? null);
    setInfo(null);
    setResult(null);
    setError(null);
    setStatus('idle');
    if (!f) return;
    try {
      setInfo(await engine.probe(f));
    } catch (e) {
      setError((e as EngineError).message);
    }
  }

  async function run(name: JobName) {
    if (!file) return;
    setJobName(name);
    setStatus('running');
    setResult(null);
    setError(null);
    setProgress(null);
    setElapsed(0);
    startedAt.current = performance.now();
    const job =
      name === 'GIF'
        ? { kind: 'video-to-gif' as const, file, startSec: 0, maxDurationSec: 10, width: 480, fps: 12 }
        : {
            kind: 'video-to-video' as const,
            file,
            container: 'mp4' as const,
            videoCodec: 'avc' as const,
            audioCodec: 'aac' as const,
            targetBytes: PRESETS.discord.maxBytes,
          };
    const handle = engine.run(job, setProgress);
    jobId.current = handle.id;
    try {
      const r = await handle.result;
      setElapsed((performance.now() - startedAt.current) / 1000);
      setResult(r);
      setStatus('done');
    } catch (e) {
      setElapsed((performance.now() - startedAt.current) / 1000);
      setError((e as EngineError).message);
      setStatus('error');
    } finally {
      jobId.current = null;
    }
  }

  const browser = caps ? browserLabel(caps.userAgent) : '…';
  const matrixRow =
    jobName && (status === 'done' || status === 'error')
      ? `| ${clip} → ${jobName} | ${browser} | ${status === 'done' ? 'PASS' : 'FAIL'} | ${formatSeconds(elapsed)} | ${
          result ? formatBytes(result.blob.size) : '–'
        } | ${(result?.notes ?? []).concat(error ? [error] : []).join('; ')} |`
      : null;

  return (
    <main>
      <h1>Converter spike</h1>
      <p className="muted">Test page for the conversion engine. Nothing leaves this device.</p>

      <section>
        <h2>This browser</h2>
        {!caps ? (
          <p>Checking…</p>
        ) : (
          <>
            <p>
              <strong>{browser}</strong> · WebCodecs <Yes ok={caps.webCodecs} /> · Secure page <Yes ok={caps.secureContext} />
            </p>
            {!caps.secureContext && (
              <p className="error">This page isn't on HTTPS or localhost, so video encoding is blocked. Open the deployed https link instead.</p>
            )}
            <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Can read video</th><th></th><th>Can make video</th><th></th><th>Can make audio</th><th></th></tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.max(Object.keys(caps.videoDecode).length, Object.keys(caps.videoEncode).length) }).map((_, i) => {
                  const d = Object.entries(caps.videoDecode)[i];
                  const e = Object.entries(caps.videoEncode)[i];
                  const a = Object.entries(caps.audioEncode)[i];
                  return (
                    <tr key={i}>
                      <td>{d?.[0]}</td><td>{d && <Yes ok={d[1]} />}</td>
                      <td>{e?.[0]}</td><td>{e && <Yes ok={e[1]} />}</td>
                      <td>{a?.[0]}</td><td>{a && <Yes ok={a[1]} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </section>

      <section>
        <h2>1. Pick a test clip</h2>
        <div className="row">
          <select value={clip} onChange={(e) => setClip(e.target.value as (typeof CLIPS)[number])} aria-label="Which test clip">
            {CLIPS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input type="file" accept="video/*" onChange={(e) => pick(e.target.files?.[0])} disabled={status === 'running'} />
        </div>
        {info && (
          <p className="muted">
            {info.format ?? 'Unknown format'} · {formatSeconds(info.durationSec)} · {formatBytes(info.fileSize)}
            {info.video && <> · {info.video.width}×{info.video.height} {info.video.codec} (can read: {info.video.canDecode ? 'yes' : 'no'})</>}
            {info.audio && <> · audio {info.audio.codec} (can read: {info.audio.canDecode ? 'yes' : 'no'})</>}
          </p>
        )}
      </section>

      <section>
        <h2>2. Convert</h2>
        <div className="row">
          <button onClick={() => run('MP4 <20MB')} disabled={!file || status === 'running'}>Make MP4 under 20 MB</button>
          <button onClick={() => run('GIF')} disabled={!file || status === 'running'}>Make GIF (first 10 s)</button>
          {status === 'running' && <button onClick={() => jobId.current !== null && engine.cancel(jobId.current)}>Cancel</button>}
        </div>
        {status === 'running' && (
          <div style={{ marginTop: 12 }}>
            <progress value={progress?.fraction ?? 0} max={1} />
            <p className="muted" aria-live="polite">
              {progress?.stage ?? 'Starting'} · {Math.round((progress?.fraction ?? 0) * 100)}% · {formatSeconds(elapsed)}
            </p>
          </div>
        )}
        {error && <p className="error" role="alert">{error}</p>}
      </section>

      {result && resultUrl && (
        <section>
          <h2>3. Result</h2>
          <p>
            <strong>{formatBytes(result.blob.size)}</strong>
            {result.targetBytes && <> of {formatBytes(result.targetBytes)}</>} · {formatSeconds(elapsed)} · {result.attempts} attempt
            {result.attempts === 1 ? '' : 's'}
          </p>
          <ul className="muted">{result.notes.map((n) => <li key={n}>{n}</li>)}</ul>
          {result.mimeType.startsWith('video/') ? (
            <video src={resultUrl} controls playsInline />
          ) : (
            <img className="preview" src={resultUrl} alt="GIF result" />
          )}
          <p>
            <a href={resultUrl} download={result.fileName}>Download {result.fileName}</a>
          </p>
          <p className="muted">In the Claude preview link, downloads are blocked. They work on the deployed site.</p>
        </section>
      )}

      {matrixRow && (
        <section>
          <h2>Test result row</h2>
          <p className="muted">Paste this into docs/SPIKE-RESULTS.md, or send it back to Claude.</p>
          <pre>{matrixRow}</pre>
          <button onClick={() => navigator.clipboard?.writeText(matrixRow).catch(() => undefined)}>Copy row</button>
        </section>
      )}
    </main>
  );
}
